import { useEffect, useRef, useState } from "react";

import { useWorkspaceConnections } from "@web-app/api";
import { useAlert } from "@web-app/components";
import { AppFilterType, Order, User } from "@web-app/types";
import { dayjs } from "@web-app/utils/time";

import { INITIAL_APP_FILTER_DATA } from "./data";

export default function useOrdersFilterDropdown({
  appFilter,
  setAppFilter,
}: {
  appFilter: AppFilterType;
  setAppFilter: (appFilter: AppFilterType) => void;
}) {
  const alert = useAlert();
  const { data: connections } = useWorkspaceConnections();

  const [temporaryAppFilter, setTemporaryAppFilter] =
    useState<AppFilterType>(appFilter);
  const popoverFilterButtonRef = useRef<HTMLButtonElement>(null);
  const [tabgroupDefaultIndex, setTabGroupDefaultIndex] = useState<
    number | null
  >(null);
  const [assigneeSearch, setAssigneeSearch] = useState("");

  useEffect(() => {
    if (temporaryAppFilter.countryCode !== appFilter.countryCode) {
      setTemporaryAppFilter({
        ...temporaryAppFilter,
        countryCode: appFilter.countryCode,
      });
    }
  }, [appFilter.countryCode, temporaryAppFilter]);

  useEffect(() => {
    if (appFilter.stateIds?.length) {
      setTemporaryAppFilter({
        ...temporaryAppFilter,
        stateIds: appFilter.stateIds,
      });
    }
  }, [appFilter.stateIds?.length]);

  const clickPopoverFilterButton = () => {
    popoverFilterButtonRef.current?.click();
  };

  const openAppFilterPopover = (index: number) => {
    clickPopoverFilterButton();
    setTabGroupDefaultIndex(index);
  };

  const formattedText = (count: number, text: string) =>
    count > 1 ? `${count} ${text}s` : `${count} ${text}`;

  const onChange = (data: Partial<AppFilterType>, apply = false) => {
    setTemporaryAppFilter({ ...temporaryAppFilter, ...data });

    if (apply) {
      setAppFilter({ ...temporaryAppFilter, ...data });
    }
  };

  const onReset = () => {
    setTemporaryAppFilter(INITIAL_APP_FILTER_DATA);
    setAppFilter(INITIAL_APP_FILTER_DATA);

    clickPopoverFilterButton();
  };

  const onApply = () => {
    const startDate = dayjs(temporaryAppFilter.scheduledDateTimeStart);
    const endDate = dayjs(temporaryAppFilter.scheduledDateTimeEnd);

    // Check if dates are within 2 months of each other
    const twoMonthsFromStart = startDate.add(2, "month");
    const twoMonthsBeforeStart = startDate.subtract(2, "month");

    if (
      !endDate.isBetween(twoMonthsBeforeStart, twoMonthsFromStart, null, "[]")
    ) {
      alert.showAlert({
        message: "Date range cannot exceed 2 months",
        title: "Invalid Date Range",
        type: "error",
      });
      return;
    }

    setAppFilter(temporaryAppFilter);
    clickPopoverFilterButton();
  };

  const onChangeOrderStatus = (value: string, status: Order["status"]) => {
    onChange({
      orderStatus:
        value === "true"
          ? [...temporaryAppFilter.orderStatus, status]
          : temporaryAppFilter.orderStatus.filter((s) => status !== s),
    });
  };

  const onChangeDriverStatus = (
    value: string,
    status: User["driverDetails"]["dutyStatus"],
  ) =>
    onChange({
      driverStatus:
        value === "true"
          ? [...temporaryAppFilter.driverStatus, status]
          : temporaryAppFilter.driverStatus.filter((s) => status !== s),
    });

  const onChangeDeliveryType = (value: string, deliveryType: Order["type"]) =>
    onChange({
      orderDeliveryType:
        value === "true"
          ? [...temporaryAppFilter.orderDeliveryType, deliveryType]
          : temporaryAppFilter.orderDeliveryType.filter(
              (s) => deliveryType !== s,
            ),
    });

  const onChangeConnection = (value: string, connectionId: string) => {
    onChange({
      workspaceIds:
        value === "true"
          ? [...temporaryAppFilter.workspaceIds, connectionId]
          : temporaryAppFilter.workspaceIds.filter((a) => a !== connectionId),
    });
  };

  const statusCount =
    appFilter.orderStatus.length + appFilter.driverStatus.length;
  const deliveryTypeCount = appFilter.orderDeliveryType.length;
  const assigneeCount = appFilter.assigneeIds.length;
  const stateCount = appFilter.stateIds?.length || 0;
  const connectionCount = appFilter.workspaceIds.length;

  return {
    assigneeCount,
    assigneeSearch,
    clickPopoverFilterButton,
    connectionCount,
    connections,
    deliveryTypeCount,
    formattedText,
    onApply,
    onChange,
    onChangeConnection,
    onChangeDeliveryType,
    onChangeDriverStatus,
    onChangeOrderStatus,
    onReset,
    openAppFilterPopover,
    popoverFilterButtonRef,
    setAssigneeSearch,
    setTabGroupDefaultIndex,
    setTemporaryAppFilter,
    stateCount,
    statusCount,
    tabgroupDefaultIndex,
    temporaryAppFilter,
  };
}
